<template>
  <div class="admin-container">
    <div class="admin-header">
      <h1>School List</h1>
    </div>
    <div class="border-container-unique37">
      <div class="admin-list-title">List of School List</div>
      <div>
        You can view school, add school to YSA program and remove school from
        YSA program.
      </div>

      <div class="admin-flex-container" ref="adminFlexContainer">
        <div class="admin-search-container">
          <input
            type="text"
            class="admin-search-input"
            placeholder="Search..."
            v-model="searchQuery"
            @input="handleSearchChange"
          />
          <img
            src="/images/maker-cart/union.svg"
            alt="Search"
            class="admin-search-icon"
          />
        </div>
      </div>
      <div class="admin-list-tool-container"></div>

      <div class="mb-3" style="display: flex; gap: 10px; flex-wrap: wrap"></div>

      <TableData
        :headers="headers"
        :items="tableItems"
        :loading="tableLoading"
        :total-pages="data?.data?.total_pages || 0"
        :current-page="currentPage"
        @update:current-page="changePage"
        :is-action="true"
        :is-edit-action="false"
        :is-delete-action="false"
        @custom-action="handleCustomAction"
        @sort="handleTableSort"
        :use-api-sort="true"
        :customActions="customActions"
        :show-data-info="true"
        :total-rows="data?.data?.total_rows || 0"
        :current-limit="filters.limit"
        variant="rounded"
      />
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { onMounted, ref, computed, watch, inject } from "vue";
import YsaAdminService from "@/services/ysaAdminService";
import { debounce } from "lodash";
import TableData from "@/components/common/TableData.vue";
import { useUrlQuerySync } from "@/composeables/useUrlQuerySync";

const tableLoading = ref(true);
const searchQuery = ref("");
const notify = inject("notify");

const getCategory = (isChangemaker, isInnovator) => {
  const category = [];
  if (isChangemaker) {
    category.push("Sustainability Youth Changemaker");
  }
  if (isInnovator) {
    category.push("Sustainability Youth Innovator");
  }
  return category.length > 0 ? category.join(", ") : "-";
};

const data = ref({
  message: "",
  error: null,
  data: {
    page: 1,
    limit: 10,
    total_rows: 0,
    total_pages: 0,
    rows: [],
  },
});

const currentPage = ref(1);
const filters = ref({
  search: "",
  page: 1,
  limit: 10,
  sort_by: "title",
  order: "asc",
});

useUrlQuerySync({
  filters,
  excludeDefaults: [{ page: 1 }, { limit: 10 }],
});

onMounted(async () => {
  await getAll();
  window.addEventListener("resize", updateLayout);
  updateLayout();
});

// Define table headers
const headers = ref([
  { text: "Title", value: "title", sortable: true },
  { text: "Slug", value: "slug", sortable: true },
  { text: "Category", value: "category", sortable: false },
]);

// Map API data to table items
const tableItems = computed(() => {
  return (data.value?.data?.rows || []).map((item) => ({
    id: item.id,
    title: item.title || "-",
    slug: item.slug || "-",
    category: getCategory(item.is_changemaker, item.is_innovator),
    isYsa: item.special_value === "ysa",
  }));
});

const getAll = async () => {
  try {
    tableLoading.value = true;

    const response = await YsaAdminService.getAllSchool(filters.value);
    if (response.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    console.log(error.message);
  } finally {
    tableLoading.value = false;
  }
};

// Sync search query with filters.search
watch(
  () => filters.value.search,
  (newValue) => {
    searchQuery.value = newValue || "";
  }
);

// Debounced search handler
const handleSearchChange = debounce(() => {
  filters.value.search = searchQuery.value;
  filters.value.page = 1;
  currentPage.value = 1;
  getAll();
}, 300);

const changePage = (page) => {
  currentPage.value = page;
  filters.value.page = page;
  getAll();
};

const handleTableSort = ({ sort_by, sort_dir }) => {
  filters.value.sort_by = sort_by;
  filters.value.order = sort_dir.toLowerCase();
  filters.value.page = 1;
  currentPage.value = 1;
  getAll();
};

// Function to update layout based on window width
const updateLayout = () => {
  const container = document.querySelector(".admin-flex-container");
  const toolContainer = document.querySelector(".admin-list-tool-container");
  const parentContainer = document.querySelector(".border-container-unique37");
  const width = window.innerWidth;

  if (width <= 991) {
    parentContainer.insertBefore(toolContainer, container.nextSibling);
  } else {
    container.appendChild(toolContainer);
    container.insertBefore(toolContainer, container.children[1]);
  }
};

// Toggle YSA membership
const toggleYSA = async (id) => {
  const response = await YsaAdminService.toggleSchoolYSA(id);
  if (response.status === 200) {
    notify && notify.success("Updated school YSA status", "Success");
    await getAll();
  }
};

function parseSerializedString(serializedString) {
  const regex = /s:\d+:"([^"]+)";s:\d+:"([^"]+)";/g;
  let match;
  const result = {};

  while ((match = regex.exec(serializedString)) !== null) {
    if (match.index === regex.lastIndex) {
      regex.lastIndex++;
    }

    const key = match[1];
    const value = match[2];

    if (key === "user_display") {
      result.user_display = value;
    } else if (key === "post_title") {
      result.post_title = value;
    }
  }

  return result;
}

// Custom actions for TableData
const customActions = ref([
  {
    type: "toggle-ysa",
    icon: (item) =>
      item.isYsa ? "/images/icon/subtract.svg" : "/images/icon/plusmark.svg",
  },
]);

const handleCustomAction = async (action) => {
  if (action.type === "toggle-ysa") {
    await toggleYSA(action.item.id);
  }
};

function convertTimestamp(timestamp) {
  // Buat objek Date dari timestamp
  var date = new Date(timestamp * 1000);

  // Format tanggal dan waktu
  var year = date.getFullYear();
  var month = ("0" + (date.getMonth() + 1)).slice(-2);
  var day = ("0" + date.getDate()).slice(-2);
  var hours = ("0" + date.getHours()).slice(-2);
  var minutes = ("0" + date.getMinutes()).slice(-2);
  var seconds = ("0" + date.getSeconds()).slice(-2);

  // Gabungkan hasil format
  var formattedDate =
    year +
    "-" +
    month +
    "-" +
    day +
    " " +
    hours +
    ":" +
    minutes +
    ":" +
    seconds;
  return formattedDate;
}
</script>
